import { loadStripe } from '@stripe/stripe-js';
import api from './apiClient';

// Stripe configuration
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Initialize Stripe (this is safe to do on the client side)
let stripePromise = null;

/**
 * Get Stripe instance - lazy loaded for security
 */
export const getStripe = () => {
    if (!stripePromise) {
        if (!STRIPE_PUBLISHABLE_KEY) {
            console.error('❌ STRIPE_PUBLISHABLE_KEY not found in environment variables');
            throw new Error('Stripe configuration missing');
        }

        // Validate publishable key format
        if (!STRIPE_PUBLISHABLE_KEY.startsWith('pk_')) {
            console.error('❌ Invalid Stripe publishable key format');
            throw new Error('Invalid Stripe key format');
        }

        stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
        console.log('✅ Stripe initialized with publishable key:',
            STRIPE_PUBLISHABLE_KEY.substring(0, 12) + '...');
    }

    return stripePromise;
};

/**
 * Create Payment Intent on backend
 */
export const createPaymentIntent = async (orderData) => {
    try {
        const requestData = {
            amount: Math.round(orderData.totalAmount * 100), // Convert to cents
            currency: orderData.currency || 'eur',
            orderId: orderData.orderId,
            metadata: {
                orderId: orderData.orderId,
                customerEmail: orderData.customerEmail,
            }
        };

        const response = await api.post('/payments/create-intent', requestData);
        return response;
    } catch (error) {
        console.error('❌ Error creating payment intent:', error);
        throw error;
    }
};

/**
 * Confirm payment with Stripe
 */
export const confirmPayment = async (stripe, elements, clientSecret, paymentData) => {
    try {
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams: {
                return_url: `${window.location.origin}/checkout/success`,
                payment_method_data: {
                    billing_details: {
                        name: paymentData.customerName,
                        email: paymentData.customerEmail,
                        phone: paymentData.customerPhone,
                        address: paymentData.billingAddress ? {
                            line1: paymentData.billingAddress.line1,
                            line2: paymentData.billingAddress.line2,
                            city: paymentData.billingAddress.city,
                            postal_code: paymentData.billingAddress.postalCode,
                            country: paymentData.billingAddress.country,
                        } : undefined,
                    },
                },
            },
            redirect: 'if_required', // Only redirect for 3D Secure
        });

        if (error) {
            console.error('❌ Payment confirmation error:', error);
            return { error, paymentIntent: null };
        }

        console.log('✅ Payment confirmed:', paymentIntent);
        return { error: null, paymentIntent };
    } catch (error) {
        console.error('❌ Payment confirmation failed:', error);
        return { error, paymentIntent: null };
    }
};

/**
 * Handle 3D Secure authentication
 */
export const handleCardAction = async (stripe, clientSecret) => {
    try {
        const { error, paymentIntent } = await stripe.handleCardAction(clientSecret);

        if (error) {
            console.error('❌ 3D Secure authentication failed:', error);
            return { error, paymentIntent: null };
        }

        console.log('✅ 3D Secure authentication successful:', paymentIntent);
        return { error: null, paymentIntent };
    } catch (error) {
        console.error('❌ 3D Secure handling failed:', error);
        return { error, paymentIntent: null };
    }
};

/**
 * Get payment status from backend
 */
export const getPaymentStatus = async (paymentIntentId) => {
    try {
        const response = await api.get(`/payments/status/${paymentIntentId}`);
        return response;
    } catch (error) {
        console.error('❌ Error getting payment status:', error);
        throw error;
    }
};

/**
 * Create Stripe Checkout Session (alternative to Elements)
 */
export const createCheckoutSession = async (orderData) => {
    try {
        const requestData = {
            orderId: orderData.orderId,
            successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/checkout/cancel`,
        };

        const response = await api.post('/payments/create-checkout', requestData);
        return response;
    } catch (error) {
        console.error('❌ Error creating checkout session:', error);
        throw error;
    }
};

/**
 * Redirect to Stripe Checkout
 */
export const redirectToCheckout = async (sessionId) => {
    try {
        const stripe = await getStripe();
        const { error } = await stripe.redirectToCheckout({ sessionId });

        if (error) {
            console.error('❌ Redirect to checkout failed:', error);
            throw error;
        }
    } catch (error) {
        console.error('❌ Checkout redirect error:', error);
        throw error;
    }
};

/**
 * Stripe payment method types configuration
 */
export const PAYMENT_METHODS = {
    CARD: 'card',
    SEPA_DEBIT: 'sepa_debit',
    GIROPAY: 'giropay',
    SOFORT: 'sofort',
};

/**
 * Test card numbers for development
 */
export const TEST_CARDS = {
    VISA_SUCCESS: '4242424242424242',
    VISA_3DS: '4000002500003155',
    VISA_DECLINED: '4000000000000002',
    MASTERCARD_SUCCESS: '5555555555554444',
    AMEX_SUCCESS: '378282246310005',
};

/**
 * Format Stripe error messages for user display
 */
export const formatStripeError = (error) => {
    const errorMessages = {
        card_declined: 'Your card was declined. Please try a different card.',
        insufficient_funds: 'Your card has insufficient funds.',
        incorrect_cvc: 'Your card\'s security code is incorrect.',
        expired_card: 'Your card has expired.',
        processing_error: 'An error occurred while processing your card. Please try again.',
        authentication_required: 'Additional authentication is required. Please follow the instructions.',
        generic_decline: 'Your card was declined. Please contact your bank for more information.',
    };

    return errorMessages[error.code] || error.message || 'An unexpected error occurred.';
};

const stripeApi = {
    getStripe,
    createPaymentIntent,
    confirmPayment,
    handleCardAction,
    getPaymentStatus,
    createCheckoutSession,
    redirectToCheckout,
    formatStripeError,
    PAYMENT_METHODS,
    TEST_CARDS,
};

export default stripeApi;
